import { ArrowRight, Euro, Percent, Shield, SprayCan } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CollapsibleSection } from '@/components/ui/collapsible-section';
import { NoCurrencyMarker } from '@/components/common/NoCurrencyMarker';
import { CURRENCY_NOT_SET_LABEL, formatMoney } from '@/lib/format-money';
import type { SyncedLocationPricing } from '@/types/api';

interface PricingInfoCardProps {
  pricing?: SyncedLocationPricing;
  /** If true, renders as a collapsible section */
  defaultCollapsed?: boolean;
}

function formatCurrency(amount: number | null, currency: string | null): string {
  return amount === null ? '—' : formatMoney(amount, currency);
}

/**
 * One partner amount in the currency it is stored in. With no currency (the
 * partner sent none) the bare number carries an inline marker, so the reader
 * does not take it for a price in some assumed currency.
 */
function PartnerAmount({ amount, currency, testId }: { amount: number | null; currency: string | null; testId: string }): JSX.Element {
  return (
    <span className="font-medium" data-testid={testId}>
      {formatCurrency(amount, currency)}
      {amount !== null && currency === null && <NoCurrencyMarker />}
    </span>
  );
}

function formatPercentage(factor: number | null): string {
  if (factor === null) {
    return '—';
  }
  // Factor is stored as decimal (e.g., 0.9 = 10% discount)
  const discount = (1 - factor) * 100;
  if (discount > 0) {
    return `${discount.toFixed(0)}% off`;
  } else if (discount < 0) {
    return `${Math.abs(discount).toFixed(0)}% premium`;
  }
  return 'No change';
}

export function PricingInfoCard({ pricing, defaultCollapsed }: PricingInfoCardProps): JSX.Element {
  const hasAnyPricing = pricing && (
    pricing.basePrice !== null ||
    pricing.cleaningFee !== null ||
    pricing.securityDeposit !== null ||
    pricing.weeklyPriceFactor !== null ||
    pricing.monthlyPriceFactor !== null
  );

  // Content to render
  const content = !pricing || !hasAnyPricing ? (
    <p className="text-sm text-muted-foreground">
      No pricing information available from partner
    </p>
  ) : (
    <div className="space-y-4">
      {/* Base Price */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Euro className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Base Price (nightly)</span>
        </div>
        <PartnerAmount amount={pricing.basePrice} currency={pricing.currency} testId="partner-base-price" />
      </div>

      {/* Cleaning Fee */}
      {pricing.cleaningFee !== null && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SprayCan className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Cleaning Fee</span>
          </div>
          <PartnerAmount amount={pricing.cleaningFee} currency={pricing.currency} testId="partner-cleaning-fee" />
        </div>
      )}

      {/* Security Deposit */}
      {pricing.securityDeposit !== null && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Security Deposit</span>
          </div>
          <PartnerAmount amount={pricing.securityDeposit} currency={pricing.currency} testId="partner-security-deposit" />
        </div>
      )}

      {/* Weekly Rate */}
      {pricing.weeklyPriceFactor !== null && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Percent className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Weekly Rate</span>
          </div>
          <span className="font-medium text-green-600">
            {formatPercentage(pricing.weeklyPriceFactor)}
          </span>
        </div>
      )}

      {/* Monthly Rate */}
      {pricing.monthlyPriceFactor !== null && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Percent className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Monthly Rate</span>
          </div>
          <span className="font-medium text-green-600">
            {formatPercentage(pricing.monthlyPriceFactor)}
          </span>
        </div>
      )}

      {/* Currency indicator with conversion info */}
      {pricing.currency ? (
        <div className="pt-2 border-t">
          {pricing.originalCurrency && pricing.originalBasePrice !== null ? (
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs">
                <span className="text-muted-foreground line-through">
                  {formatCurrency(pricing.originalBasePrice, pricing.originalCurrency)}
                </span>
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <span className="text-green-600 font-medium">
                  {formatCurrency(pricing.basePrice, pricing.currency)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Converted from {pricing.originalCurrency} during sync
              </p>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground" data-testid="partner-pricing-currency">
              Currency: {pricing.currency}
            </span>
          )}
        </div>
      ) : (
        <div className="pt-2 border-t">
          <span className="text-xs text-muted-foreground" data-testid="partner-pricing-currency">
            {CURRENCY_NOT_SET_LABEL}
          </span>
        </div>
      )}
    </div>
  );

  // Render as collapsible if defaultCollapsed is provided
  if (defaultCollapsed !== undefined) {
    return (
      <CollapsibleSection
        title="Partner Pricing"
        icon={<Euro className="h-4 w-4 text-muted-foreground" />}
        defaultCollapsed={defaultCollapsed}
      >
        {content}
      </CollapsibleSection>
    );
  }

  // Default: render as Card
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Euro className="h-5 w-5" />
          Partner Pricing
        </CardTitle>
      </CardHeader>
      <CardContent>
        {content}
      </CardContent>
    </Card>
  );
}
